import {
  selectForgotPasswordForm,
  selectLoginForm,
  selectResestPasswordForm,
  selectSignupForm,
} from "app/containers/Auth/redux/selector";
import { actions } from "app/containers/Auth/redux/slice";
import { IGoogleLogin } from "app/containers/Auth/types";
import { selectUserFormData } from "app/redux/selector";
import { actions as appActions } from "app/redux/slice";
import { ERROR_TYPE, userOnboardingStage } from "app/types";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { handleApiError, handleAuthError } from "utils/errorHandler";
import { request } from "utils/request";

export function* loginRequest(action: {
  payload: { callback?: (nextPage) => void };
}) {
  try {
    yield delay(500);
    const loginForm = yield select(selectLoginForm);
    const requestURL = `${API_URL}auth/login`;
    const options = {
      method: "POST",
      body: JSON.stringify({
        email: loginForm.email.value,
        password: loginForm.password.value,
      }),
    };
    const { data, err } = yield call(request, requestURL, options);
    if (err || !data) {
      yield put(actions.loginRequestComplete());
      const parsed = handleApiError(err, {
        showToast: false,
        returnParsed: true,
      });
      switch (parsed?.type) {
        case ERROR_TYPE.EMAIL_EXIST:
        case ERROR_TYPE.USER_BLOCKED:
        case ERROR_TYPE.INACTIVE_PERMANENT:
        case ERROR_TYPE.INACTIVE:
          yield put(
            actions.setLoginFormErrors([
              { name: "email", error: parsed.message },
            ]),
          );
          handleApiError(err, { showToast: true });
          break;
        case ERROR_TYPE.PASSWORD_MISMATCH:
        case ERROR_TYPE.PASSWORD_ATTEMPT_MAX:
        case ERROR_TYPE.PASSWORD_NOT_SET_YET:
        case ERROR_TYPE.PASSWORD_EXPIRED:
          yield put(
            actions.setLoginFormErrors([
              { name: "password", error: parsed.message },
            ]),
          );
          handleApiError(err, { showToast: true });
          break;
        default:
          handleApiError(err);
          break;
      }
      return;
    }
    yield put(
      appActions.setUserOnboardingProgress(data?.data?.userOnboardingProgress),
    );
    yield put(appActions.setUserToken(data?.data.accessToken));
    yield put(appActions.setIsAuthenticated(true));
    yield put(actions.loginRequestComplete());
    yield localStorage.setItem("accessToken", data?.data?.accessToken);

    if (action.payload.callback) {
      yield call(action.payload.callback, data?.data?.userOnboardingProgress);
    }
  } catch (err: any) {
    yield put(actions.loginRequestComplete());
    const parsed = handleApiError(err, {
      showToast: false,
      returnParsed: true,
    });
    switch (parsed?.type) {
      case ERROR_TYPE.EMAIL_EXIST:
      case ERROR_TYPE.USER_BLOCKED:
      case ERROR_TYPE.INACTIVE_PERMANENT:
      case ERROR_TYPE.INACTIVE:
        yield put(
          actions.setLoginFormErrors([
            { name: "email", error: parsed.message },
          ]),
        );
        break;
      case ERROR_TYPE.PASSWORD_MISMATCH:
      case ERROR_TYPE.PASSWORD_ATTEMPT_MAX:
      case ERROR_TYPE.PASSWORD_NOT_SET_YET:
      case ERROR_TYPE.PASSWORD_EXPIRED:
        yield put(
          actions.setLoginFormErrors([
            { name: "password", error: parsed.message },
          ]),
        );
        break;
      default:
        handleApiError(err);
        break;
    }
  }
}

export function* forgotPasswordRequest({
  payload,
}: {
  payload: { callback?: () => void };
}) {
  try {
    yield delay(500);
    const forgotPasswordForm = yield select(selectForgotPasswordForm);
    const requestURL = `${API_URL}auth/passwords/change-password`;
    const options = {
      method: "PATCH",
      body: JSON.stringify({ email: forgotPasswordForm.email.value }),
    };
    const { data, err } = yield call(request, requestURL, options);
    if (err || !data) {
      handleApiError(err);
      return;
    }
    if (data) {
      yield put(actions.forgotPasswordRequestComplete());
      payload.callback?.();
      return;
    }
    yield put(actions.resetStore());
  } catch (err: any) {
    const parsed = handleApiError(err, {
      showToast: false,
      returnParsed: true,
    });
    switch (parsed?.type) {
      case ERROR_TYPE.PASSWORD_LINK_EXPIRED:
      case ERROR_TYPE.INVALID_INPUT:
        yield put(
          actions.setForgotPasswordFormErrors([
            { name: "email", error: parsed.message },
          ]),
        );
        break;
      default:
        handleApiError(err);
        break;
    }
    yield put(actions.forgotPasswordRequestComplete());
    console.log(err);
  }
}

export function* signupRequest({
  payload,
}: {
  payload: { callback?: () => void };
}) {
  try {
    yield delay(500);
    const requestURL = `${API_URL}auth/signup`;
    const signupForm = yield select(selectSignupForm);
    const userForm = yield select(selectUserFormData);
    const options = {
      method: "POST",
      body: JSON.stringify({ email: signupForm.email.value }),
    };

    const { data, err } = yield call(request, requestURL, options);
    if (err || !data) {
      handleApiError(err);
      return;
    }

    yield put(
      appActions.setUserFormData({
        ...userForm,
        _id: data.data._id,
      }),
    );
    yield put(actions.signupRequestComplete());
    payload.callback?.();
    yield put(actions.resetStore());
  } catch (err: any) {
    yield put(actions.signupRequestComplete());
    const parsed = handleApiError(err, {
      showToast: false,
      returnParsed: true,
    });

    switch (parsed?.type) {
      case ERROR_TYPE.USER_ALREADY_EXISTS:
        yield put(
          actions.setSignupFormErrors([
            { name: "email", error: parsed.message },
          ]),
        );
        break;

      default:
        handleApiError(err);
        break;
    }
  }
}

export function* verifyEmailRequest({
  payload,
}: {
  payload: { code: string; callback?: () => void };
}) {
  try {
    yield delay(500);
    const requestURL = `${API_URL}auth/verification`;
    const params = new URLSearchParams(window.location.search);
    const userForm = yield select(selectUserFormData);
    const token = params.get("hash");
    const tokenData = {
      token: token,
    };
    const formData = {
      verificationCode: payload?.code,
      id: userForm?._id,
    };
    const options = {
      method: "POST",
      body: JSON.stringify(token ? tokenData : formData),
    };
    const { data, err } = yield call(request, requestURL, options);
    if (err || !data) {
      const parsed = handleApiError(err, {
        showToast: false,
        returnParsed: true,
      });
      const messageFromApi = parsed?.message?.trim();
      if (messageFromApi) {
        yield put(actions.setVerifyEmailFormError(messageFromApi));
      }
      switch (parsed?.type) {
        case ERROR_TYPE.INVALID_TOKEN:
        case ERROR_TYPE.TOKEN_EXPIRED:
        case ERROR_TYPE.INVALID_ID:
        case ERROR_TYPE.VERIFICATION_CODE_EXPIRED:
        case ERROR_TYPE.INVALID_OTP:
        case ERROR_TYPE.USER_ALREADY_VERIFIED:
          break;
        default:
          handleApiError(err, { showToast: true });
          break;
      }
      yield put(actions.verifyEmailRequestComplete());
      return;
    }

    yield put(appActions.setUserToken(data?.data.accessToken));
    yield put(
      appActions.setUserOnboardingProgress(data?.data?.userOnboardingProgress),
    );
    yield localStorage.setItem("accessToken", data?.data?.accessToken);
    payload.callback?.();
    yield put(actions.resetStore());
  } catch (err: any) {
    yield put(actions.verifyEmailRequestComplete());
    const parsed = handleApiError(err, {
      showToast: false,
      returnParsed: true,
    });
    const messageFromApi = parsed?.message?.trim();
    if (messageFromApi) {
      yield put(actions.setVerifyEmailFormError(messageFromApi));
    }
    switch (parsed?.type) {
      case ERROR_TYPE.INVALID_TOKEN:
      case ERROR_TYPE.TOKEN_EXPIRED:
      case ERROR_TYPE.INVALID_ID:
      case ERROR_TYPE.VERIFICATION_CODE_EXPIRED:
      case ERROR_TYPE.INVALID_OTP:
      case ERROR_TYPE.USER_ALREADY_VERIFIED:
        break;
      default:
        handleApiError(err, { showToast: true });
        break;
    }
  }
}

export function* resendVerificationRequest({
  payload,
}: {
  payload: { email: string };
}) {
  try {
    yield delay(500);
    const requestURL = `${API_URL}auth/signup`;
    const options = {
      method: "POST",
      body: JSON.stringify({ email: payload.email }),
    };
    const { data, err } = yield call(request, requestURL, options);
    if (err || !data) {
      const parsed = handleApiError(err, {
        showToast: false,
        returnParsed: true,
      });
      if (parsed?.type === ERROR_TYPE.USER_ALREADY_EXISTS) {
        yield put(actions.resetVerifyEmailFormError());
        return;
      }
      handleApiError(err);
      return;
    }
    yield put(actions.resetVerifyEmailFormError());
  } catch (err) {
    handleApiError(err);
  } finally {
    yield put(actions.resendVerificationRequestComplete());
  }
}

export function* resetPasswordRequest({
  payload,
}: {
  payload: { callback?: () => void };
}) {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("hash");
    const email = params.get("email");
    yield delay(500);
    const resetPasswordForm = yield select(selectResestPasswordForm);
    const requestURL = `${API_URL}auth/passwords/change-password`;
    const options = {
      method: "PATCH",
      body: JSON.stringify({
        newPassword: resetPasswordForm.password.value,
        email: email,
        token: token,
      }),
    };
    const { data, err } = yield call(request, requestURL, options);
    if (err || !data) {
      throw handleApiError(err);
    }
    yield put(actions.resetPasswordRequestComplete());
    payload.callback?.();
    yield put(actions.resetStore());
  } catch (err: any) {
    yield put(actions.resetPasswordRequestComplete());
    handleApiError(err);
  }
}

export function* doGoogleLoginRequest(action: {
  payload: {
    data: IGoogleLogin;
    callback: (
      nextPage: userOnboardingStage,
      fullName: string,
      token: string,
    ) => void;
  };
}) {
  try {
    yield delay(500);
    yield put(actions.setIsisGoogleLogin(true));
    const requestURL = `${API_URL}auth/google-login`;
    const options = {
      method: "POST",
      body: JSON.stringify(action.payload.data),
    };
    const { data, err } = yield call(request, requestURL, options);
    if (err || !data) {
      throw handleApiError(err);
    }
    yield put(actions.setIsisGoogleLogin(false));
    yield localStorage.setItem("accessToken", data?.data?.accessToken);
    yield put(appActions.setUserToken(data?.data.accessToken));
    yield put(
      appActions.setUserOnboardingProgress(data?.data?.userOnboardingProgress),
    );
    yield call(
      yield call(
        action.payload.callback,
        data?.data?.userOnboardingProgress,
        data?.data?.fullName,
        data?.data?.accessToken,
      ),
    );
  } catch (err: any) {
    yield put(actions.setIsisGoogleLogin(false));
    handleApiError(err);
  }
}

export function* authSaga() {
  yield takeLatest(actions.loginRequest, loginRequest);
  yield takeLatest(actions.forgotPasswordRequest, forgotPasswordRequest);
  yield takeLatest(actions.signupRequest, signupRequest);
  yield takeLatest(actions.verifyEmailRequest, verifyEmailRequest);
  yield takeLatest(actions.resendVerificationRequest, resendVerificationRequest);
  yield takeLatest(actions.resetPasswordRequest, resetPasswordRequest);
  yield takeLatest(actions.doGoogleLogin, doGoogleLoginRequest);
}
